import { css } from "@emotion/react";
import {
  colors,
  timings,
  breakpoints,
} from "@mytutor/shared-react-web-components/lib/theme";
import { PrimaryCTAButton } from "@mytutor/shared-react-web-components/lib/PrimaryCTA";
import React, { useState, useEffect, useRef } from "react";
import Image from "next/image";
import { useMultiSelectFilterState } from "../../hooks/useMultiSelectFilter";

const style = css`
  width: 100%;
  position: relative;

  .dropdownTriggerButton {
    border: 1px solid ${colors.neutral4};
    background-color: ${colors.neutral1};
    text-align: left;
    font-size: 14px;
    border-radius: 4px;
    height: 48px;
    padding: 12px 16px 12px 8px;
    display: grid;
    grid-template-columns: 1fr 20px;
    align-items: center;
    justify-content: flex-start;
    overflow: hidden;
    width: 100%;
    transition: ${timings.medium};
    text-transform: capitalize;

    &:hover,
    &:focus {
      border: 1px ${colors.primary6} solid;
      outline: none;
    }

    .triggerButtonText {
      color: #333333;
      font-family: "FoundersGrotesk", sans-serif;
      font-weight: 500;
      font-size: 16px;
      height: fit-content;
      display: flex;
      justify-content: flex-start;
      flex-wrap: nowrap;
      overflow: hidden;
      width: 100%;

      @media (max-width: ${breakpoints.phone}px) {
        height: auto;
      }

      > p {
        color: #333333;
      }
    }
  }

  .dropdownContainer {
    background-color: ${colors.neutral1};
    box-shadow: 0 6px 6px hsla(0, 0%, 50%, 0.5);
    max-width: 200%;
    padding: 1em;
    width: 100vw;
    height: 100dvh;
    top: 0;
    left: 0;
    position: fixed;
    z-index: 2147483002;
    display: grid;
    grid-template-rows: 40px minmax(0, 1fr) 90px;
    align-items: flex-start;

    @media (min-width: ${breakpoints.tablet}px) {
      position: absolute;
      width: 100%;
      min-width: 350px;
      height: auto;
      max-width: 50vw;
      top: 100%;
      left: 1px;
      display: block;
    }
    @media (min-width: ${breakpoints.desktop}px) {
      max-width: 60vw;
    }
  }
  .dropdownContainerHidden {
    display: none;
  }

  .dropdownContainer {
     padding-top: 1em; 
    @media (min-width: 481px) and (max-width: 767px) {
      padding-top: 5em;
    }
  }

  .dropdownFooter {
    display: grid;
    grid-template-columns: 1fr;
    row-gap: 10px;
    justify-items: center;
    align-items: center;

    @media (min-width: ${breakpoints.tablet}px) {
      row-gap: 0;
      grid-template-columns: 1fr 1fr;
    }

    .resetLink {
      display: inline-block;
      font-family: "FoundersGrotesk", sans-serif;
      margin: 0 auto;
      padding: 0.25em;
      font-size: 16px;
      font-weight: 500;
      cursor: pointer;
      color: #03776e;
      border: none;
      background: none;
      outline: 0;
    }
  }

  .mobileHeading {
    display: grid;
    grid-template-columns: 60px 1fr 60px;
    justify-content: center;
    align-items: center;
    font-size: 18px;
    font-weight: 500;

    @media (min-width: ${breakpoints.tablet}px) {
      display: none;
    }

    & h2 {
      grid-column: 2;
      text-align: center;
      margin: 0;
      font-size: 22px;
    }

    & .filterClose {
      grid-column: 3;
      justify-self: flex-end;
      position: static;
      margin: 0;
    }
  }

  .availabilityGrid {
    display: grid;
    grid-template-rows: 24px;
    row-gap: 10px;
    padding: 0 0 20px 0;

    .headerRow {
      white-space: nowrap;
    }

    .availabilityGridRow {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr 1fr;
      justify-items: center;
      font-size: 14px;

      div:first-of-type {
        justify-self: flex-start;
        font-weight: 500;
      }

      input[type="checkbox"] {
        transition: all 0.15s cubic-bezier(0, 1.05, 0.72, 1.07);
        border-radius: 0.25em;
        border: 1px solid ${colors.neutral11};
        width: 24px;
        height: 24px;
        color: ${colors.neutral1};
      }
      input[type="checkbox"]:checked,
      input[type="checkbox"]:checked:hover {
        accent-color: ${colors.primary5};
        color: ${colors.neutral1};
      }
      input[type="checkbox"]:hover,
      input[type="checkbox"]:focus {
        border-color: ${colors.primary4};
      }
    }
  }

  .applyButton {
    font-size: 1rem;
    font-weight: 500;
  }
`;
const availabilityOptions = [
  "mon-0000-1200",
  "mon-1200-1700",
  "mon-1700-2359",
  "tue-0000-1200",
  "tue-1200-1700",
  "tue-1700-2359",
  "wed-0000-1200",
  "wed-1200-1700",
  "wed-1700-2359",
  "thu-0000-1200",
  "thu-1200-1700",
  "thu-1700-2359",
  "fri-0000-1200",
  "fri-1200-1700",
  "fri-1700-2359",
  "sat-0000-1200",
  "sat-1200-1700",
  "sat-1700-2359",
  "sun-0000-1200",
  "sun-1200-1700",
  "sun-1700-2359",
];
const daysOfWeek = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const selectAllInit = {
  "all-0000-1200": false,
  "all-1200-1700": false,
  "all-1700-2359": false,
};

interface UrlParameterProps {
  urlParameters: UrlParams;
}
interface UrlParams {
  availability?: string;
}
const AvailabilityFilter = ({ urlParameters }: UrlParameterProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const args = {
    filterName: "availability",
    initVal: urlParameters.availability,
    selectAllInit: selectAllInit,
    allowedOptions: availabilityOptions,
    isOpen: isOpen,
    setIsOpen: setIsOpen,
  };

  const {
    currentSelection,
    setCurrentSelection,
    appliedSelection,
    handleChange,
    selectAllState,
    handleSelectAll,
    handleToggle,
    handleSubmit,
    handleReset,
  } = useMultiSelectFilterState(args);

  const refComponentContainer = useRef(null);

  useEffect(() => {
    const componentContainer = refComponentContainer.current;

    function handleClickOutside(event: MouseEvent) {
      if (
        isOpen &&
        componentContainer &&
        !componentContainer.contains(event.target)
      ) {
        setCurrentSelection(appliedSelection);
        setIsOpen(false);
      }
    }

    // Bind the event listener
    if (isOpen) {
      document.addEventListener("click", handleClickOutside);
    }
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("click", handleClickOutside);
    };
  }, [appliedSelection, isOpen, setCurrentSelection]);

  const getCells = (index: number) => {
    const keyStart = index * 3;
    const cells = [];
    for (let i = 0; i < 3; i++) {
      cells.push(
        <div
          className="availabilityGridCell"
          key={availabilityOptions[keyStart + i]}
        >
          <input
            type="checkbox"
            name="availability"
            value={availabilityOptions[keyStart + i]}
            checked={currentSelection[availabilityOptions[keyStart + i]]}
            onChange={(e) =>
              handleChange({
                value: e.currentTarget.value,
                checkboxState: e.currentTarget.checked,
              })
            }
          />
        </div>
      );
    }
    return cells;
  };

  const getSelectedDays = () => {
    const selectedTimeslots = availabilityOptions.filter(
      (a) =>
        urlParameters.availability && urlParameters.availability.includes(a)
    );

    if (!selectedTimeslots.length) {
      return <span>All availability</span>;
    }
    const selectedDays = selectedTimeslots.map((d) => d.slice(0, 3));

    const uniqueOnly = (value, index, self) => {
      return self.indexOf(value) === index;
    };

    const uniqueSelectedDays = selectedDays.filter(uniqueOnly);

    return uniqueSelectedDays.join(", ");
  };

  return (
    <>
      <div
        className="availabilityWrapper"
        // eslint-disable-next-line react/no-unknown-property
        css={style}
        ref={refComponentContainer}
      >
        <button className="dropdownTriggerButton" onClick={handleToggle}>
          <div className="triggerButtonText" data-cy="availabilityFilter">
            <p>{getSelectedDays()}</p>
          </div>
          <Image
            src="/view-tutors/chevron-vertical-primary7.svg"
            alt="ICON: Open/Close dropdown"
            height={8}
            width={16}
          />
        </button>

        <section
          className={isOpen ? "dropdownContainer" : "dropdownContainerHidden"}
        >
          <header className="mobileHeading">
            <h2>Availability</h2>

            <div className="filterClose">
              <Image
                src="/view-tutors/icon.svg"
                height={13}
                width={13}
                onClick={handleToggle}
                alt="A closing icon"
              />
            </div>
          </header>
          <div className="dropdownContent">
            <div className="availabilityGrid">
              <div className="availabilityGridRow headerRow">
                <div className="availabilityGridCell"></div>
                <div className="availabilityGridCell">Before 12pm</div>
                <div className="availabilityGridCell">12-5pm</div>
                <div className="availabilityGridCell">After 5pm</div>
              </div>
              <div className="availabilityGridRow">
                <div className="availabilityGridCell">Select all</div>
                <div className="availabilityGridCell">
                  <input
                    type="checkbox"
                    name="availability"
                    value="all-0000-1200"
                    checked={selectAllState["all-0000-1200"]}
                    onChange={(e) =>
                      handleSelectAll({
                        value: e.currentTarget.value,
                        checkboxState: e.currentTarget.checked,
                      })
                    }
                  />
                </div>
                <div className="availabilityGridCell">
                  <input
                    type="checkbox"
                    name="availability"
                    value="all-1200-1700"
                    checked={selectAllState["all-1200-1700"]}
                    onChange={(e) =>
                      handleSelectAll({
                        value: e.currentTarget.value,
                        checkboxState: e.currentTarget.checked,
                      })
                    }
                  />
                </div>
                <div className="availabilityGridCell">
                  <input
                    type="checkbox"
                    name="availability"
                    value="all-1700-2359"
                    checked={selectAllState["all-1700-2359"]}
                    onChange={(e) =>
                      handleSelectAll({
                        value: e.currentTarget.value,
                        checkboxState: e.currentTarget.checked,
                      })
                    }
                  />
                </div>
              </div>

              {daysOfWeek.map((item, index) => (
                <div className="availabilityGridRow" key={item}>
                  <div className="availabilityGridCell">{item}</div>
                  {getCells(index)}
                </div>
              ))}
            </div>
          </div>

          <footer className="dropdownFooter">
            <PrimaryCTAButton
              className="applyButton"
              onClick={() => handleSubmit("_")}
            >
              Apply filters
            </PrimaryCTAButton>
            <button className="resetLink" onClick={handleReset}>
              Clear filters
            </button>
          </footer>
        </section>
      </div>
    </>
  );
};

export default AvailabilityFilter;
